// src/context/tenantAuth.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';

type TenantAuthContextType = {
  isLoggedIn: boolean;
  loading: boolean;
  user: any | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const TenantAuthContext = createContext<TenantAuthContextType | undefined>(
  undefined
);

export const TenantAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // helper to fetch current user info
  const fetchUser = async () => {
    try {
      const info = await Auth.currentUserInfo();
      setUser(info);
    } catch (e) {
      setUser(null);
    }
  };

  // On mount: check if already signed in
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchUser();
      setLoading(false);
    })();
  }, []);

  const login = async (username: string, password: string) => {
    setLoading(true);
    try {
      // sign in with Cognito
      await Auth.signIn({ username, password });

      // now fetch and store user in context
      await fetchUser();
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await Auth.signOut();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const value: TenantAuthContextType = {
    isLoggedIn: !!user,
    loading,
    user,
    login,
    logout,
  };

  return (
    <TenantAuthContext.Provider value={value}>
      {children}
    </TenantAuthContext.Provider>
  );
};

export const useTenantAuth = (): TenantAuthContextType => {
  const ctx = useContext(TenantAuthContext);
  if (!ctx) {
    throw new Error('useTenantAuth must be used within TenantAuthProvider');
  }
  return ctx;
};
